import type { ComponentType } from "react";
import { useFormState } from "react-hook-form";
import type { Control } from "react-hook-form";
import ErrorText from "@/components/ErrorText/ErrorText";
import { combineClasses } from "@/lib/utils";
import styles from "./InputWithLabelAndError.module.scss";

interface WithLabelAndErrorProps {
  controlType?: string | undefined;
  errorMessage?: string | undefined;
  isRequired?: boolean | undefined;
  control?: Control<any> | undefined;
  label?: string | undefined;
  name: string;
  // "caption": small, uppercase, muted-gray, letter-spaced — the label
  // style used by this app's modals (Export, Add Member) and stat cards,
  // as opposed to "default"'s sentence-case field label (e.g. Login).
  labelVariant?: "default" | "caption" | undefined;
  disabled?: boolean | undefined;
  isDisabled?: boolean | undefined;
}

const withLabelAndError = <P extends object>(
  InputComponent: ComponentType<P>,
) => {
  const InputWithLabelAndError = (allProps: P & WithLabelAndErrorProps) => {
    const { controlType, errorMessage, isRequired, labelVariant = "default", disabled, isDisabled: propIsDisabled, ...rest } = allProps;
    const isFieldDisabled = Boolean(disabled || propIsDisabled);
    // Read via `rest`, not a destructuring that would exclude them — `rest`
    // (spread onto InputComponent below) must still carry `name`/`control`
    // /`label` down to the actual control, e.g. so `register(name, ...)`
    // gets a real field name instead of `undefined`.
    const { control, label, name } = rest;
    // `control` is only present for react-hook-form-controlled fields, and
    // it doesn't change between defined/undefined across renders of a given
    // usage site, but this is still a conditional hook call.
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { errors } = control ? useFormState({ control, name }) : { errors: undefined };

    const labelHtml = (
      <label
        className={combineClasses(
          styles.label,
          labelVariant === "caption" && styles.labelCaption,
          isFieldDisabled && styles.labelDisabled,
        )}
        htmlFor={name}
      >
        {label}{" "}
        {isRequired ? <span className={styles.requiredMark}>*</span> : ""}
      </label>
    );

    const fieldError = (errors as Record<string, { message?: string } | undefined> | undefined)?.[
      name
    ];
    const errorHtml = <ErrorText>{fieldError?.message || errorMessage || ""}</ErrorText>;

    return controlType === "checkbox" ? (
      <div className={combineClasses(styles.checkboxControl, isFieldDisabled && styles.controlDisabled)}>
        <div className={styles.checkboxContainer}>
          <InputComponent {...(rest as unknown as P)} disabled={isFieldDisabled} />
          {labelHtml}
        </div>
        {errorHtml}
      </div>
    ) : (
      <div
        className={combineClasses(
          styles.inputControl,
          controlType === "textarea" && styles.textareaControl,
          isFieldDisabled && styles.controlDisabled,
        )}
      >
        {labelHtml}
        <InputComponent {...(rest as unknown as P)} disabled={isFieldDisabled} />
        {errorHtml}
      </div>
    );
  };

  return InputWithLabelAndError;
};

export default withLabelAndError;
